/**
 * The single "is this thread CONFIRMED Trashed" lookup, shared by every
 * consumer that gates on it — the Reply Queue's bulk membership exclusion
 * (`src/hooks/useReplyQueue.ts`) and the Lead Dossier's single-draft
 * actionable-continuation check (`src/hooks/useLeadDossier.ts`) — so they
 * cannot independently diverge on Trash lifecycle the way F1/F2 did before
 * this module existed.
 *
 * Fail-closed, chunked (`os_conversation_threads.deleted_at`): if a
 * requested thread id doesn't resolve (RLS/concurrency-unknown), that is
 * never treated as "not deleted" — see `fetchContactArchivalStatus` in
 * useReplyQueue.ts for the same established pattern.
 */

import { supabase } from '@/lib/supabase';

const LOOKUP_CHUNK_SIZE = 50;

function chunkArray<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * Filters out rows whose thread is CONFIRMED Trashed — the exact "queue
 * membership" exclusion rule, extracted so a second consumer (the Lead
 * Dossier's winner selection, F4) can apply the IDENTICAL rule in the
 * IDENTICAL position (before picking a winner) instead of reimplementing
 * the `thread_id === null || !deletedByThread.get(thread_id)` line a second
 * time — which is exactly how F4 happened: the queue excluded Trashed
 * candidates before dedup/winner selection, the dossier picked a winner
 * across ALL threads first and only then checked whether THAT winner was
 * Trashed, so a lead with drafts on two threads (one org/lead/campaign is
 * unique per `supabase/migrations/017_conversation_threads.sql`, not one
 * thread per lead) could make the two surfaces disagree on which draft is
 * even being discussed.
 *
 * `thread_id === null` rows are NEVER excluded here — see
 * `fetchThreadDeletionStatus`'s callers for why (a null thread means "no
 * thread to have been Trashed", not "unknown/hidden").
 */
export async function excludeConfirmedTrashedThreads<T extends { thread_id: string | null }>(
  rows: readonly T[],
  organizationId: string
): Promise<T[]> {
  const threadIds = rows.map((row) => row.thread_id).filter((id): id is string => id !== null);
  if (threadIds.length === 0) return [...rows];

  const deletedByThread = await fetchThreadDeletionStatus(threadIds, organizationId);
  return rows.filter((row) => row.thread_id === null || !deletedByThread.get(row.thread_id));
}

export async function fetchThreadDeletionStatus(
  threadIds: readonly string[],
  organizationId: string
): Promise<Map<string, boolean>> {
  const uniqueIds = Array.from(new Set(threadIds));
  const deletedByThread = new Map<string, boolean>();

  for (const chunk of chunkArray(uniqueIds, LOOKUP_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from('os_conversation_threads')
      .select('id, deleted_at')
      .eq('organization_id', organizationId)
      .in('id', chunk);
    if (error) throw error;

    for (const thread of (data ?? []) as Array<{ id: string; deleted_at: string | null }>) {
      deletedByThread.set(thread.id, thread.deleted_at !== null);
    }
  }

  const missing = uniqueIds.filter((id) => !deletedByThread.has(id));
  if (missing.length > 0) {
    throw new Error(
      `${missing.length} thread(s) did not resolve in the soft-delete lookup — refusing to report a lifecycle state that could silently include a Trashed or RLS-hidden thread.`
    );
  }

  return deletedByThread;
}
